import { Link } from "react-router-dom";
import { Download, ArrowRight, BookOpen, MessageCircle, ArrowDown } from "lucide-react";
import { MaskedLine, FadeIn, Reveal } from "@/components/Reveal";
import SectionHeader from "@/components/SectionHeader";
import CTASection from "@/components/CTASection";
import { CONTACT, CUSTOMERS, IMG } from "@/data/content";
import { CATALOGUE_SECTIONS, CATALOGUE_STATEMENTS, PDF_PATH } from "@/data/catalogue";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const DownloadButton = ({ testid, light = false }) => (
  <a
    href={PDF_PATH}
    download="Amazing-Grace-Product-Catalogue.pdf"
    data-testid={testid}
    className={`group inline-flex items-center justify-center gap-3 text-[13px] tracking-[0.12em] uppercase px-8 py-4 transition-colors duration-300 ${
      light
        ? "bg-ivory text-navy hover:bg-gold"
        : "bg-navy text-ivory hover:bg-gold hover:text-navy"
    }`}
  >
    <Download size={16} />
    Download Product Catalogue
  </a>
);

const Cover = () => (
  <section data-testid="catalogue-cover" className="relative min-h-[86vh] flex items-end overflow-hidden bg-navy">
    <div className="absolute inset-0">
      <img src="/catalogue/cover.jpg" alt="Amazing Grace product catalogue — luxury hotel bedding" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-navy/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/25 to-transparent" />
    </div>
    <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pb-20 pt-48 w-full">
      <FadeIn delay={0.2}>
        <div className="flex items-center gap-4 mb-6">
          <span className="h-px w-10 bg-gold" />
          <span className="text-[11px] tracking-[0.32em] uppercase text-ivory/75">
            Amazing Grace — Hospitality Textiles & Hotel Essentials
          </span>
        </div>
      </FadeIn>
      <h1 className="font-serif font-medium tracking-tight text-5xl sm:text-6xl md:text-7xl text-ivory leading-[1.02] max-w-4xl">
        <MaskedLine delay={0.3}>Hotel Linen Product</MaskedLine>
        <MaskedLine delay={0.45}>
          <span className="italic text-gold">Catalogue</span>
        </MaskedLine>
      </h1>
      <FadeIn delay={0.7}>
        <p className="mt-6 text-ivory/80 text-base md:text-lg font-light max-w-2xl leading-relaxed">
          Quality Products for Exceptional Hospitality Experiences
        </p>
        <div className="flex flex-col sm:flex-row gap-4 mt-10">
          <DownloadButton testid="catalogue-hero-download-btn" light />
          <button
            onClick={() => scrollToId("catalogue-contents")}
            data-testid="catalogue-hero-browse-btn"
            className="group inline-flex items-center justify-center gap-2 border border-ivory/50 text-ivory text-[13px] tracking-[0.12em] uppercase px-8 py-4 transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            Browse Catalogue
            <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-1" />
          </button>
        </div>
      </FadeIn>
    </div>
  </section>
);

const AboutStrip = () => (
  <section className="py-20 md:py-28 border-b border-navy/10">
    <div className="max-w-[1100px] mx-auto px-6 md:px-12 text-center">
      <Reveal>
        <BookOpen size={28} strokeWidth={1.5} className="text-gold mx-auto" />
        <h2 className="font-serif text-3xl md:text-4xl font-medium text-navy mt-6">About Amazing Grace</h2>
        <p className="mt-6 text-navy/70 text-base md:text-lg font-light leading-relaxed max-w-3xl mx-auto">
          Amazing Grace is a manufacturer and supplier of hospitality textiles and hotel essentials based in
          Coimbatore, Tamil Nadu. We serve hospitality businesses with products designed for hotel, resort,
          institutional and commercial requirements — from bed linen and duvets to towels, robes and room accents.
        </p>
        <p className="mt-4 text-navy/60 text-sm md:text-base font-light leading-relaxed max-w-2xl mx-auto">
          This catalogue is price-free by design. Specifications are shared for reference; contact our team for
          current pricing and availability.
        </p>
      </Reveal>
    </div>
  </section>
);

const Contents = () => (
  <section id="catalogue-contents" className="py-20 md:py-28 bg-linen/40">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <SectionHeader number="—" eyebrow="Table of Contents" title="Inside the Catalogue" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-navy/10 border border-navy/10 mt-14">
        {CATALOGUE_SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => scrollToId(`cat-${s.id}`)}
            data-testid={`toc-${s.id}`}
            className="group bg-ivory p-7 flex items-center gap-5 text-left transition-colors duration-500 hover:bg-navy"
          >
            <span className="font-serif italic text-gold text-2xl">{s.num}</span>
            <span className="font-serif text-xl text-navy group-hover:text-ivory transition-colors duration-500">
              {s.title}
            </span>
            <ArrowRight size={15} className="ml-auto text-navy/30 group-hover:text-gold transition-colors duration-500" />
          </button>
        ))}
      </div>
    </div>
  </section>
);

const SpecChips = ({ items }) => (
  <ul className="flex flex-wrap gap-2 mt-4">
    {items.map((s) => (
      <li key={s} className="text-[11px] tracking-[0.1em] uppercase px-3 py-1.5 border border-navy/15 text-navy/70">
        {s}
      </li>
    ))}
  </ul>
);

const ProductCard = ({ product, sectionId, index }) => (
  <Reveal delay={index * 0.08} className="h-full">
    <div
      data-testid={`catalogue-product-${sectionId}-${index}`}
      className="group bg-ivory border border-navy/10 h-full flex flex-col"
    >
      <div className="overflow-hidden h-56">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.05]"
        />
      </div>
      <div className="p-7 flex flex-col flex-1">
        <h4 className="font-serif text-2xl font-medium text-navy">{product.name}</h4>
        <p className="text-sm text-navy/60 font-light mt-2 leading-relaxed">{product.desc}</p>
        {product.specs && <SpecChips items={product.specs} />}
        {product.threadCounts && (
          <div className="mt-5">
            <p className="text-[10px] tracking-[0.28em] uppercase text-gold">Available Thread Counts</p>
            <p className="font-serif text-lg text-navy mt-1">{product.threadCounts.join("  |  ")}</p>
          </div>
        )}
        {product.sizes && (
          <div className="mt-5">
            <p className="text-[10px] tracking-[0.28em] uppercase text-gold">Available Sizes</p>
            <div className={`grid gap-x-4 gap-y-1.5 mt-2 ${product.sizes.length > 6 ? "grid-cols-3 sm:grid-cols-4" : "grid-cols-2"}`}>
              {product.sizes.map((sz) => (
                <span key={sz} className="text-[13px] text-navy/70 whitespace-nowrap">{sz}</span>
              ))}
            </div>
          </div>
        )}
        {product.custom && (
          <p className="text-xs text-navy/50 font-light italic mt-4">
            Custom sizes and specifications available based on customer requirements.
          </p>
        )}
        <div className="mt-auto pt-6">
          <Link
            to={`/quote?product=${encodeURIComponent(product.quoteAs)}`}
            data-testid={`quote-btn-${sectionId}-${index}`}
            className="group/btn inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-navy border-b border-gold pb-1 hover:text-gold transition-colors duration-300"
          >
            Request a Quote
            <ArrowRight size={13} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
          </Link>
        </div>
      </div>
    </div>
  </Reveal>
);

const CategorySection = ({ section, dark }) => (
  <section id={`cat-${section.id}`} data-testid={`catalogue-section-${section.id}`} className={`py-20 md:py-28 scroll-mt-28 ${dark ? "bg-navy" : ""}`}>
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <SectionHeader number={section.num} eyebrow="Collection" title={section.title} dark={dark} />
        <p className={`text-base font-light leading-relaxed max-w-md ${dark ? "text-ivory/60" : "text-navy/60"}`}>
          {section.intro}
        </p>
      </div>
      <div className={`grid gap-6 mt-14 ${section.products.length === 1 ? "lg:grid-cols-2" : section.products.length === 2 ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3"}`}>
        {section.products.map((p, i) => (
          <ProductCard key={p.name} product={p} sectionId={section.id} index={i} />
        ))}
      </div>
    </div>
  </section>
);

const CustomizationCTA = () => (
  <section data-testid="catalogue-customization" className="py-20 md:py-28 bg-linen/40 border-y border-navy/10">
    <div className="max-w-[1100px] mx-auto px-6 md:px-12 text-center">
      <Reveal>
        <p className="text-[11px] tracking-[0.32em] uppercase text-gold">Customization Available</p>
        <h2 className="font-serif text-3xl md:text-5xl font-medium text-navy mt-5 leading-tight">
          Custom Sizes & Specifications, <span className="italic text-gold">Made for You</span>
        </h2>
        <p className="mt-6 text-navy/70 text-base md:text-lg font-light leading-relaxed max-w-2xl mx-auto">
          Custom sizes and specifications can be produced based on customer requirements. Contact our team to
          discuss your specific hotel or hospitality requirements.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
          <Link
            to="/quote"
            data-testid="catalogue-request-customization-btn"
            className="group inline-flex items-center justify-center gap-2 bg-navy text-ivory text-[13px] tracking-[0.12em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy"
          >
            Request Customization
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="catalogue-whatsapp-btn"
            className="inline-flex items-center justify-center gap-2 border border-navy text-navy text-[13px] tracking-[0.12em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-navy hover:text-ivory"
          >
            <MessageCircle size={16} />
            WhatsApp Enquiry
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

const Trusted = () => (
  <section data-testid="catalogue-trusted" className="py-20 md:py-28">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <SectionHeader
        number="—"
        eyebrow="Our Customers"
        title="Trusted by Leading Hospitality Brands"
        description="We are delighted to serve and supply hospitality products to reputed names across the hospitality industry."
        align="center"
      />
      <div className="flex flex-wrap justify-center gap-x-10 gap-y-6 mt-14">
        {[...CUSTOMERS, "Other reputed hospitality customers"].map((name) => (
          <span key={name} className="font-serif text-xl md:text-2xl text-navy/75">{name}</span>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-px bg-navy/10 border border-navy/10 mt-16">
        {CATALOGUE_STATEMENTS.map((s) => (
          <div key={s} className="bg-ivory p-10">
            <span className="font-serif italic text-gold text-3xl leading-none">"</span>
            <p className="font-serif text-lg text-navy/80 leading-relaxed mt-2">{s}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const DownloadBand = () => (
  <section data-testid="catalogue-download-band" className="py-20 md:py-24 bg-navy">
    <div className="max-w-[1100px] mx-auto px-6 md:px-12 text-center">
      <Reveal>
        <Download size={26} strokeWidth={1.5} className="text-gold mx-auto" />
        <h2 className="font-serif text-3xl md:text-4xl font-medium text-ivory mt-6">
          Take the Catalogue <span className="italic text-gold">With You</span>
        </h2>
        <p className="mt-4 text-ivory/65 text-base font-light max-w-xl mx-auto">
          Download the complete Amazing Grace Product Catalogue as a PDF — ready to share over WhatsApp, email or print.
        </p>
        <div className="mt-8 flex justify-center">
          <DownloadButton testid="catalogue-footer-download-btn" light />
        </div>
      </Reveal>
    </div>
  </section>
);

const Catalogue = () => (
  <main data-testid="catalogue-page">
    <Cover />
    <AboutStrip />
    <Contents />
    {CATALOGUE_SECTIONS.map((s, i) => (
      <CategorySection key={s.id} section={s} dark={false} />
    ))}
    <CustomizationCTA />
    <Trusted />
    <DownloadBand />
    <CTASection
      title="Ready to Discuss Your Requirement?"
      text="Connect with Amazing Grace for quality hospitality textiles and hotel essentials."
      image={IMG.lobby}
    />
  </main>
);

export default Catalogue;
